import * as amplitude from "@amplitude/analytics-browser";
import { env } from "../Utils/env";
import { useAsaSession } from "./AsaSessionServices";
import { useEffect } from "react";
import { useFintechLayouts } from "./AsaApiServices";
import { IAsaSession } from "../types/types";

// Global variables to store session and layout data and resolve promises
let globalSessionData: IAsaSession | null = null;
let globalLayoutData: {appName: string} = {appName: ""};
let sessionResolve: ((value: { session: IAsaSession; layout: {appName: string} }) => void) | null = null;

const setGlobalSessionData = (session: IAsaSession, layout: {appName: string}) => {
  globalSessionData = session;
  globalLayoutData = layout;
  if (sessionResolve) {
    sessionResolve({ session, layout });
    sessionResolve = null;
  }
};

// Promise that resolves when session and layout are available
const waitForSession = (): Promise<{ session: IAsaSession; layout: {appName: string} }> => {
  if (globalSessionData) {
    return Promise.resolve({ session: globalSessionData, layout: globalLayoutData });
  }
  return new Promise((resolve) => {
    sessionResolve = resolve;
  });
};

export enum AnalyticsAction {
  Click = "Tap",
  View = "View",
  API_Result = "API Result",
}

export enum AnalyticsFeature {
  OneGoal = "OneGoal",
  Subscription = "Subscription",
  SubscriptionManagement = "SubscriptionManagement",
  Payment = "Payment",
  BillingAddress = "BillingAddress",
  KidPay = "KidPay",
  MoneyPatrol = "MoneyPatrol",
  Guac = "Guac",
  PortFolioPilot = "PortFolioPilot",
  Tykr = "Tykr",
  HFK = "HFK",
  NudgeMoney = "NudgeMoney",
  AsaTransfer = "AsaTransfer",
  CRB = "Credit Rent Boost",
  Authentication = "Authentication",
}

export const AnalyticsService = {
  track: (
    eventName: string,
    action: AnalyticsAction,
    featureName: AnalyticsFeature,
    extraProperties?: Record<string, unknown>,
  ) => {

    const executeTrack = ({ session, layout }: { session: IAsaSession; layout: {appName: string} }) => {
      const extraProps = extraProperties || {};

      extraProps["AsaPayFintechApplicationCode"] = session.fintechApplicationCode?.toString() ?? "";
      extraProps["AsaPayFintechCode"] = session.asaFintechCode?.toString() ?? "";
      extraProps["AsaPayFintechAppName"] = layout.appName;

      amplitude
        .track("ASAPay " + eventName, {
          action,
          featureName,
          ...extraProps,
        })
        .promise.then((response) => {
          console.log("track session", response);
        })
        .catch((error) => {
          console.log("track session error", error);
        })
        .finally(() => {
          console.log("track session finally");
        });
    };

    // Wait for session or execute immediately if available
    waitForSession().then(executeTrack);
  },
};

function AnalyticsComponent() {
  const { data: session } = useAsaSession();
  const layout = useFintechLayouts();

  useEffect(() => {
    // Set global session and layout data for track function
    if (session && layout) {
      setGlobalSessionData(session, layout);
      amplitude.setUserId(session.asaConsumerCode.toString());
    }

    const targetPage = session?.sessionPages?.find(page => page.pageName === 'AnalyticsAPIKey')
    if(targetPage){
      const apikey = targetPage.pageData
      amplitude.init(apikey, {
        autocapture: false,
      });
    }

  }, [session, layout]);

  return null;
}

export default AnalyticsComponent;
